import { useState, useRef, useEffect, useCallback } from "react";
import { API, api } from "@/lib/api";
import { toast } from "sonner";

/**
 * useAIChat — owns all AI Assistant state + SSE streaming + session CRUD.
 *
 * Returned API:
 *   messages, input, setInput, provider, setProvider, sessionId,
 *   busy, sessions, loadSessions, newChat, loadSession, deleteSession, send, scrollRef
 */
export function useAIChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "NEXUS online. How can I help you build today?" },
  ]);
  const [input, setInput] = useState("");
  const [provider, setProvider] = useState("anthropic");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const r = await api.get("/ai/sessions");
      setSessions(r.data);
    } catch (e) {
      console.error("[useAIChat] loadSessions failed", e);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Auto-scroll on new messages.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const newChat = useCallback(() => {
    setSessionId(null);
    setMessages([{ role: "assistant", text: "Fresh session. Where to next?" }]);
  }, []);

  const loadSession = useCallback(async (sid) => {
    try {
      const r = await api.get(`/ai/sessions/${sid}`);
      setSessionId(r.data.session_id);
      setProvider(r.data.provider || "anthropic");
      setMessages(r.data.messages);
    } catch {
      toast.error("Couldn't load session");
    }
  }, []);

  const deleteSession = useCallback(async (sid, e) => {
    e?.stopPropagation?.();
    try {
      await api.delete(`/ai/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.session_id !== sid));
      if (sid === sessionId) newChat();
      toast.success("Session deleted");
    } catch (err) {
      console.error("[useAIChat] deleteSession failed", err);
      toast.error("Couldn't delete session");
    }
  }, [sessionId, newChat]);

  const send = useCallback(async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, provider, session_id: sessionId || undefined }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Stream failed");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const block of parts) {
          let evt = "message";
          let data = "";
          for (const line of block.split("\n")) {
            if (line.startsWith("event:")) evt = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
          if (!data) continue;
          try {
            const parsed = JSON.parse(data);
            if (evt === "meta" && parsed.session_id) setSessionId(parsed.session_id);
            else if (evt === "delta" && parsed.content) {
              setMessages((m) => {
                const arr = [...m];
                arr[arr.length - 1] = { role: "assistant", text: arr[arr.length - 1].text + parsed.content };
                return arr;
              });
            } else if (evt === "error") {
              toast.error(parsed.error || "Stream error");
            }
          } catch (e) {
            console.warn("[useAIChat] could not parse SSE chunk", e);
          }
        }
      }
      loadSessions();
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "AI unreachable");
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }, [input, busy, provider, sessionId, loadSessions]);

  return {
    messages, input, setInput, provider, setProvider, sessionId,
    busy, sessions, loadSessions, newChat, loadSession, deleteSession, send, scrollRef,
  };
}
